import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Paper, CircularProgress, Alert, List, ListItem, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import SkiRequestsSection from './SkiRequestsSection';
import AthleteReadOnlyView from './AthleteReadOnlyView';
import { getTeamEvents, createTeamEvent, getEventSignups, signupForEvent, leaveEvent, getSkiRequests } from '../api';
import { useAuth } from '../AuthContext';

export default function EventsSection({ team, athleteView, onSkiRequestSubmitted }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', location: '', start_date: '', end_date: '', notes: '' });
  const [signups, setSignups] = useState({}); // eventId -> [athletes]
  const [mySignups, setMySignups] = useState({}); // eventId -> true if signed up
  const [joining, setJoining] = useState({}); // eventId -> loading state
  const [eventModal, setEventModal] = useState(null); // event object or null
  const [eventTab, setEventTab] = useState(0);
  const [leaveConfirm, setLeaveConfirm] = useState({ open: false, eventId: null, eventName: '' });
  const [viewAthlete, setViewAthlete] = useState(null);
  const [pendingRequests, setPendingRequests] = useState({}); // eventId -> count of pending requests

  // Function to update pending requests for a specific event
  const updatePendingRequestsForEvent = async (eventId) => {
    try {
      const requests = await getSkiRequests(team.id, eventId);
      const today = new Date().toISOString().split('T')[0];
      const count = requests.filter(r => {
        return r.date >= today && (!r.submissions || r.submissions.length === 0);
      }).length;
      setPendingRequests(prev => ({ ...prev, [eventId]: count }));
      
      // Also call the parent callback to update team notifications
      if (onSkiRequestSubmitted) {
        onSkiRequestSubmitted();
      }
    } catch {
      setPendingRequests(prev => ({ ...prev, [eventId]: 0 }));
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line
  }, [team]);

  // Fetch signups for all events in coach view when events change
  useEffect(() => {
    if (!athleteView && events.length > 0) {
      const fetchAllSignups = async () => {
        const all = {};
        await Promise.all(events.map(async (event) => {
          try {
            const res = await getEventSignups(event.id);
            all[event.id] = res;
          } catch {
            all[event.id] = [];
          }
        }));
        setSignups(all);
      };
      fetchAllSignups();
    }
  }, [events, athleteView]);

  // Refresh mySignups when events change for athletes
  useEffect(() => {
    if (athleteView && events.length > 0) {
      const fetchMySignups = async () => {
        const signupsStatus = {};
        await Promise.all(events.map(async (event) => {
          try {
            const res = await getEventSignups(event.id, true); // checkOwn=true for athletes
            signupsStatus[event.id] = res.some(a => a.id === user.id);
          } catch {
            signupsStatus[event.id] = false;
          }
        }));
        setMySignups(signupsStatus);
      };
      fetchMySignups();
    }
  }, [events, athleteView, user.id]);

  // Fetch pending ski requests for each event (for athletes)
  useEffect(() => {
    if (athleteView && events.length > 0) {
      const fetchPendingRequests = async () => {
        const pending = {};
        await Promise.all(events.map(async (event) => {
          try {
            const requests = await getSkiRequests(team.id, event.id);
            const today = new Date().toISOString().split('T')[0];
            // Count requests for today or future that the user hasn't submitted for
            const count = requests.filter(r => {
              return r.date >= today && (!r.submissions || r.submissions.length === 0);
            }).length;
            pending[event.id] = count;
          } catch {
            pending[event.id] = 0;
          }
        }));
        setPendingRequests(pending);
      };
      fetchPendingRequests();
    }
  }, [events, athleteView, team.id]);

  async function fetchEvents() {
    setLoading(true);
    setError('');
    try {
      const data = await getTeamEvents(team.id);
      
      // Sort by start_date ascending
      data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      // For athlete view, filter to future events only
      let filtered = data;
      if (athleteView) {
        const today = new Date();
        filtered = data.filter(e => new Date(e.start_date) >= today.setHours(0,0,0,0));
      }
      setEvents(filtered);
    } catch (e) {
      if (e.message === 'Not an approved member of this team.') {
        setError('Waiting for approval to be part of this team. You will be able to see events once your membership is approved.');
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleOpenCreate = () => {
    setForm({ name: '', location: '', start_date: '', end_date: '', notes: '' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await createTeamEvent(team.id, form);
      setCreateOpen(false);
      fetchEvents();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleJoin = async (eventId) => {
    setJoining(j => ({ ...j, [eventId]: true }));
    setError('');
    try {
      await signupForEvent(eventId);
      setMySignups(s => ({ ...s, [eventId]: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setJoining(j => ({ ...j, [eventId]: false }));
    }
  };

  const handleLeave = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    setLeaveConfirm({ open: true, eventId, eventName: event?.name || 'this event' });
  };

  const handleLeaveConfirm = async () => {
    const eventId = leaveConfirm.eventId;
    setJoining(j => ({ ...j, [eventId]: true }));
    setError('');
    try {
      await leaveEvent(eventId);
      setMySignups(s => ({ ...s, [eventId]: false }));
      setLeaveConfirm({ open: false, eventId: null, eventName: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setJoining(j => ({ ...j, [eventId]: false }));
    }
  };

  function formatDateTime(dt) {
    if (!dt) return '';
    const d = new Date(dt);
    if (isNaN(d)) return dt;
    return d.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 2, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Events</Typography>
        {!athleteView && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create Event
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        events.length === 0 ? (
          <Typography>No {athleteView ? 'upcoming ' : ''}events{athleteView ? ' to join' : ''}.</Typography>
        ) : (
          events.map(event => (
            <Paper 
              key={event.id} 
              sx={{ 
                mb: 3, 
                p: 2, 
                borderRadius: 3, 
                boxShadow: 1, 
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 2,
                  transform: 'translateY(-1px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }} 
              onClick={() => { 
                setEventModal(event); 
                // For athletes, always set tab to 0 since they only have one tab (Ski Requests)
                // For coaches, if they have pending ski requests, open the Ski Requests tab directly
                if (athleteView) {
                  setEventTab(0);
                } else if (pendingRequests[event.id] > 0) {
                  setEventTab(1);
                } else {
                  setEventTab(0);
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{event.name}</Typography>
                </Box>
                {!athleteView && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%', width: 40, height: 40, boxShadow: 2 }}>
                      <PersonIcon sx={{ fontSize: 18, mr: 0.1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 15, ml: 0.1, lineHeight: 1 }}>
                        {signups[event.id] ? signups[event.id].length : 0}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">{event.location} • {formatDateTime(event.start_date)} to {formatDateTime(event.end_date)}</Typography>
              {event.notes && <Typography variant="body2" sx={{ mt: 1 }}>{event.notes}</Typography>}
              {athleteView && (
                <Box sx={{ mt: 2 }}>
                  {mySignups[event.id] ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleLeave(event.id); }}
                      disabled={joining[event.id]}
                    >
                      {joining[event.id] ? <CircularProgress size={18} /> : 'Leave Event'}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => { e.stopPropagation(); handleJoin(event.id); }}
                      disabled={joining[event.id]}
                    >
                      {joining[event.id] ? <CircularProgress size={18} /> : 'Join Event'}
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          ))
        )
      )}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Event</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Event Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
          <TextField label="Location" name="location" value={form.location} onChange={handleChange} fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Start Date" name="start_date" value={form.start_date} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} fullWidth required />
            <TextField label="End Date" name="end_date" value={form.end_date} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} fullWidth required />
          </Box>
          <TextField label="Notes" name="notes" value={form.notes} onChange={handleChange} fullWidth multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={22} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Event Detail Modal */}
      <Dialog open={!!eventModal} onClose={() => setEventModal(null)} maxWidth="md" fullWidth>
        {eventModal && (
          <>
            <DialogTitle>{eventModal.name}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {eventModal.location} • {formatDateTime(eventModal.start_date)} to {formatDateTime(eventModal.end_date)}
                </Typography>
                {eventModal.notes && <Typography variant="body2" sx={{ mt: 1 }}>{eventModal.notes}</Typography>}
              </Box>
              <Tabs value={eventTab} onChange={(_, v) => setEventTab(v)}>
                {!athleteView && <Tab label="Signups" />}
                <Tab label="Ski Requests" />
              </Tabs>
              <Box sx={{ mt: 2 }}>
                {eventTab === 0 && !athleteView && (
                  <>
                    <Typography variant="subtitle2">Signed Up Athletes:</Typography>
                    {signups[eventModal.id] && signups[eventModal.id].length === 0 ? (
                      <Typography color="text.secondary">No athletes have signed up yet.</Typography>
                    ) : (
                      <List dense>
                        {signups[eventModal.id] && signups[eventModal.id].map(a => (
                          <ListItem key={a.id} button onClick={() => setViewAthlete(a)}>
                            <ListItemText primary={a.name} secondary={a.email} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
                {(eventTab === 1 || athleteView) && (
                  <SkiRequestsSection 
                    team={team} 
                    eventId={eventModal.id} 
                    onRequestSubmitted={() => updatePendingRequestsForEvent(eventModal.id)}
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEventModal(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Leave Event Confirmation Dialog */}
      <Dialog open={leaveConfirm.open} onClose={() => setLeaveConfirm({ open: false, eventId: null, eventName: '' })}>
        <DialogTitle>Leave Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to leave "{leaveConfirm.eventName}"? You can rejoin later if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveConfirm({ open: false, eventId: null, eventName: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveConfirm} 
            variant="contained" 
            color="error"
            disabled={joining[leaveConfirm.eventId]}
          >
            {joining[leaveConfirm.eventId] ? <CircularProgress size={20} /> : 'Leave Event'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Athlete Detail Modal */}
      {viewAthlete && (
        <AthleteReadOnlyView
          open={!!viewAthlete}
          onClose={() => setViewAthlete(null)}
          athlete={viewAthlete}
          team={team}
        />
      )}
    </Box>
  );
} 